import React, { JSX, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";

type AllowedTags = keyof JSX.IntrinsicElements;

type Props = {
    text: string;
    className?: string;
    as?: AllowedTags;
    noFlexGrow?: boolean;
    /**
     * If you want to override the default text shown
     */
    label?: string;
    children?: React.ReactNode;
};

/**
 * Simple and easy to use functional component to add a copy icon next to text that allows a user to click and auto-copy
 * to the users clipboard.
 *
 * @param text
 * @param className
 * @param Component
 * @param noFlexGrow
 * @param rest
 * @constructor
 */
export const ClickToCopy: React.FC<Props> = ({ label, text, className, as: Component = "div", noFlexGrow, children, ...rest }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                // Use modern Clipboard API if available
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for older browsers
                const textArea = document.createElement("textarea");
                textArea.value = text;

                // Move textarea off-screen
                textArea.style.position = "fixed";
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";
                document.body.appendChild(textArea);

                // Select and copy the text
                textArea.select();
                document.execCommand("copy");

                // Clean up
                textArea.remove();
            }

            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    return (
        <div className="d-flex align-items-center gap-2">
            {children
                ? children
                : React.createElement(
                      Component,
                      {
                          className: `${noFlexGrow ? "" : "flex-grow-1"} ${className || ""}`,
                          ...rest,
                      },
                      label ? label : text
                  )}
            <OverlayTrigger placement="top" overlay={<Tooltip>{copied ? "Copied!" : "Copy to clipboard"}</Tooltip>}>
                <Button
                    variant="transparent"
                    size="sm"
                    onClick={handleCopy}
                    className="border-0 d-flex align-items-center justify-content-center"
                    style={{
                        transition: "all 0.2s ease-in-out",
                        width: "32px",
                        height: "32px",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                    }}>
                    {copied ? <i className="fas fa-check text-success"></i> : <i className="fas fa-copy text-secondary"></i>}
                </Button>
            </OverlayTrigger>
        </div>
    );
};
